// ControlListPage — control list for the selected category.
// Rendered in the right panel of ControlLayout.
// Route: /t/:slug/controls (index route)

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useSelectedCategory } from './ControlLayout'
import {
  controlsApi,
  controlLifecycleStateLabels,
  controlLifecycleStateColors,
  type Control,
} from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CreateControlDialog } from './CreateControlDialog'
import {
  Plus,
  Search,
  AlertTriangle,
  ChevronRight,
  X,
  Link2,
} from 'lucide-react'
import { SortableHeader, useSortState, type SortState } from '@/components/shared/SortableHeader'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// --- Sort column type ---

type SortColumn = 'name' | 'owner' | 'category' | 'risks' | 'status'

function sortControls(controls: Control[], sort: SortState<SortColumn>): Control[] {
  if (!sort.column) return controls
  const sorted = [...controls].sort((a, b) => {
    let cmp = 0
    switch (sort.column) {
      case 'name':
        cmp = a.name.localeCompare(b.name)
        break
      case 'owner':
        cmp = (a.owner_display_name ?? '').localeCompare(b.owner_display_name ?? '')
        break
      case 'category':
        cmp = a.category_name.localeCompare(b.category_name)
        break
      case 'risks':
        cmp = a.linked_risk_count - b.linked_risk_count
        break
      case 'status': {
        const order = ['draft', 'active', 'retired']
        cmp = order.indexOf(a.lifecycle_state) - order.indexOf(b.lifecycle_state)
        break
      }
    }
    return sort.direction === 'desc' ? -cmp : cmp
  })
  return sorted
}

export function ControlListPage() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const { categoryId, categoryName } = useSelectedCategory()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  function handleControlClick(controlId: string) {
    navigate({ to: `/t/${slug}/controls/${controlId}` })
  }

  if (!categoryId) {
    return <EmptySelection />
  }

  return (
    <>
      <ControlListPanel
        categoryId={categoryId}
        categoryName={categoryName}
        onControlClick={handleControlClick}
        onCreateClick={() => setCreateDialogOpen(true)}
        slug={slug}
      />

      <CreateControlDialog
        categoryId={categoryId}
        categoryName={categoryName}
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(controlId) => {
          setCreateDialogOpen(false)
          handleControlClick(controlId)
        }}
      />
    </>
  )
}

// --- Empty state when no category is selected ---

function EmptySelection() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <div className="space-y-2 text-center">
        <ChevronRight className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm">Select a category from the tree to view its controls.</p>
      </div>
    </div>
  )
}

// --- Control list panel ---

interface ControlListPanelProps {
  categoryId: string
  categoryName: string
  onControlClick: (controlId: string) => void
  onCreateClick: () => void
  slug: string
}

function ControlListPanel({
  categoryId,
  categoryName,
  onControlClick,
  onCreateClick,
  slug,
}: ControlListPanelProps) {
  const api = useApi()
  const ctrlApi = controlsApi(api)

  // Filter state
  const [nameFilter, setNameFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Sort state
  const [sort, , handleSort] = useSortState<SortColumn>()

  // Server-side filters
  const serverFilter = {
    category_id: categoryId,
    ...(nameFilter ? { search: nameFilter } : {}),
    ...(statusFilter ? { lifecycle_state: statusFilter } : {}),
  }

  const {
    data: controls,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...queryKeys.controls.all(slug), serverFilter],
    queryFn: () => ctrlApi.list(serverFilter),
  })

  // Client-side sorting
  const displayControls = useMemo(() => {
    if (!controls) return []
    return sortControls(controls, sort)
  }, [controls, sort])

  const hasActiveFilters = nameFilter !== '' || statusFilter !== ''

  const clearFilters = useCallback(() => {
    setNameFilter('')
    setStatusFilter('')
  }, [])

  return (
    <>
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 border-b p-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold">{categoryName}</h1>
          <p className="text-xs text-muted-foreground">
            {controls
              ? `${displayControls.length}${displayControls.length !== controls.length ? ` of ${controls.length}` : ''} control${controls.length !== 1 ? 's' : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button size="sm" onClick={onCreateClick}>
            <Plus className="mr-1 h-4 w-4" />
            New Control
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 border-b bg-muted/10 px-4 py-2">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by name..."
            className="h-8 pl-8 text-sm"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>
        <select
          className="h-8 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="retired">Retired</option>
        </select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={clearFilters}>
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Column headers */}
      {controls && controls.length > 0 && (
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center border-b bg-muted/30 px-4 py-1.5 text-xs font-medium text-muted-foreground">
            <SortableHeader
              label="Control"
              column="name"
              sort={sort}
              onSort={handleSort}
              className="w-[30%] min-w-0"
            />
            <SortableHeader
              label="Category"
              column="category"
              sort={sort}
              onSort={handleSort}
              className="w-[20%]"
            />
            <SortableHeader
              label="Owner"
              column="owner"
              sort={sort}
              onSort={handleSort}
              className="w-[18%]"
            />
            <SortableHeader
              label="Risks"
              column="risks"
              sort={sort}
              onSort={handleSort}
              className="w-[8%] justify-center"
            />
            <span className="w-[8%] text-center">Flags</span>
            <SortableHeader
              label="Status"
              column="status"
              sort={sort}
              onSort={handleSort}
              className="w-20 justify-center"
            />
            <span className="w-20 text-right">Code</span>
          </div>
        </TooltipProvider>
      )}

      {/* Control list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <TableSkeleton rows={6} />}

        {error && (
          <div className="p-4">
            <ErrorState
              message="Failed to load controls"
              technicalDetail={error.message}
              onRetry={() => refetch()}
            />
          </div>
        )}

        {controls && displayControls.length === 0 && (
          <div className="flex h-48 items-center justify-center text-muted-foreground">
            <p className="text-sm">
              {hasActiveFilters
                ? 'No controls match your filters.'
                : 'No controls in this category.'}
            </p>
          </div>
        )}

        {displayControls.length > 0 && (
          <div className="divide-y">
            {displayControls.map((ctrl) => (
              <ControlListRow
                key={ctrl.id}
                control={ctrl}
                onClick={() => onControlClick(ctrl.id)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// --- Individual control row ---

function ControlListRow({
  control,
  onClick,
}: {
  control: Control
  onClick: () => void
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="flex cursor-pointer items-center px-4 py-3 transition-colors hover:bg-accent/30"
        onClick={onClick}
      >
        {/* Name + description — 30% */}
        <div className="w-[30%] min-w-0">
          <div className="mb-0.5 truncate text-sm font-medium">{control.name}</div>
          {control.description && (
            <div className="truncate text-xs text-muted-foreground">{control.description}</div>
          )}
        </div>

        {/* Category — 20% */}
        <div className="w-[20%] truncate text-xs text-muted-foreground">
          {control.category_name}
        </div>

        {/* Owner — 18% */}
        <div className="w-[18%] truncate text-xs text-muted-foreground">
          {control.owner_display_name ?? (
            <span className="italic">unassigned</span>
          )}
        </div>

        {/* Linked risks — 8% */}
        <div className="flex w-[8%] items-center justify-center gap-1 text-xs tabular-nums text-muted-foreground">
          {control.linked_risk_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5">
                  <Link2 className="h-3 w-3" />
                  {control.linked_risk_count}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {control.linked_risk_count} linked risk
                  {control.linked_risk_count !== 1 ? 's' : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Flags — 8% */}
        <div className="flex w-[8%] items-center justify-center gap-2">
          {control.has_overdue_assessments && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Has overdue assessments</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Status */}
        <div className="flex w-20 justify-center">
          <Badge
            variant="outline"
            className={cn(
              'px-1.5 py-0 text-xs',
              controlLifecycleStateColors[control.lifecycle_state],
            )}
          >
            {controlLifecycleStateLabels[control.lifecycle_state] ?? control.lifecycle_state}
          </Badge>
        </div>

        {/* Short code — rightmost */}
        <div className="w-20 text-right">
          {control.short_code ? (
            <span className="font-mono text-xs text-muted-foreground">{control.short_code}</span>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
